// Project dashboard — single project view with budget vs actuals
const ProjektScreen = () => {
  const frameRef = React.useRef(null);
  const [animate, setAnimate] = React.useState(false);

  React.useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    if (!("IntersectionObserver" in window)) { setAnimate(true); return; }

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimate(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const phases = [
    { name:"Rivning", status:"klar", days:"4 dgr", spent:38, budget:42, dates:"12 – 16 maj" },
    { name:"VVS & el", status:"pågår", days:"8 dgr", spent:74, budget:68, dates:"17 – 26 maj", over:true },
    { name:"Snickeri", status:"pågår", days:"10 dgr", spent:22, budget:96, dates:"22 maj – 4 jun" },
    { name:"Montage & finish", status:"kommer", days:"5 dgr", spent:0, budget:54, dates:"5 – 12 jun" },
    { name:"Slutbesiktning", status:"kommer", days:"1 dg", spent:0, budget:8, dates:"15 jun" },
  ];

  return (
    <div ref={frameRef} className={`screen-frame ${animate ? "projekt-animate" : ""}`} data-label="Projekt dashboard">
      <ScreenChrome title="Projekt · Familjen Lindqvist" />
      <div className="screen-body projekt-body">
        <div className="projekt-head">
          <div>
            <div style={{display:'flex', gap:10, alignItems:'center'}}>
              <span className="chip warn"><span className="chip-dot"/>Pågår — dag 18 av 27</span>
              <span className="mono" style={{fontSize:12, color:'var(--ink-4)'}}>P-2026-0148</span>
            </div>
            <div className="projekt-title">Köksrenovering, Stockholmsvägen 84</div>
          </div>
          <div style={{display:'flex', gap:8}}>
            <span className="seg-btn">Tidrapporter</span>
            <span className="seg-btn">Dokument</span>
            <span className="seg-btn">Faktura</span>
          </div>
        </div>

        <div className="projekt-kpis">
          <KPI label="Kontraktsvärde" value="140 561" suffix="kr" delay={0} />
          <KPI label="Förbrukat" value="92 410" suffix="kr" sub="66 % av budget" delay={1} />
          <KPI label="Prognos vinst" value="38 200" suffix="kr" sub="TB 27 % · −4 % vs offert" warn delay={2} />
          <KPI label="Återstår" value="9 dgr" sub="Slutdatum 18 jun" delay={3} />
        </div>

        <div className="projekt-grid">
          <div className="card projekt-phases">
            <div className="card-head">
              <span style={{fontWeight:600, fontSize:14}}>Faser & budget</span>
              <span style={{fontSize:12, color:'var(--ink-3)'}}>Tim · tkr</span>
            </div>
            {phases.map((p,i) => (
              <div key={i} className="phase-row projekt-fade" style={{"--p-delay": `${0.3 + i * 0.12}s`}}>
                <div className="phase-info">
                  <div style={{display:'flex', alignItems:'center', gap:10}}>
                    <span className={`phase-status ${p.status}`}/>
                    <span style={{fontWeight:500, fontSize:14}}>{p.name}</span>
                  </div>
                  <div style={{fontSize:11.5, color:'var(--ink-4)', marginTop:4, marginLeft:18}}>{p.dates}</div>
                </div>
                <div className="phase-bar">
                  <div className="bar-track">
                    <div className="bar-fill" style={{
                      width: animate ? `${Math.min(100, (p.spent / p.budget) * 100)}%` : '0%',
                      background: p.over ? 'var(--primary)' : (p.spent === 0 ? 'var(--border-strong)' : 'var(--accent-green)'),
                      transition: `width 0.7s cubic-bezier(0.2, 0.82, 0.22, 1) ${0.4 + i * 0.12}s`
                    }}/>
                    <div className="bar-budget" style={{ left: '100%' }}/>
                  </div>
                  <div style={{display:'flex', justifyContent:'space-between', fontSize:11.5, marginTop:6, fontFamily:'var(--font-mono)', color:'var(--ink-3)'}}>
                    <span style={{color: p.over ? 'var(--primary)' : 'var(--ink-2)'}}>{p.spent} tkr</span>
                    <span>av {p.budget} tkr</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{display:'flex', flexDirection:'column', gap:16}}>
            <div className="card">
              <div className="card-head">
                <span style={{fontWeight:600, fontSize:14}}>Aktivitet idag</span>
                <span style={{fontSize:12, color:'var(--ink-3)'}}>tor 28 maj</span>
              </div>
              <div style={{padding:'4px 0 12px'}}>
                {[
                  ["07:42","Mikael L. stämplade in · VVS"],
                  ["09:15","Faktura 2026-0044 mottagen · Beijer 8 240 kr"],
                  ["11:08","Foto från arbetsplats (3) · ÄTA registrerad"],
                  ["14:30","Beställning Siemens-paket bekräftad · 12 jun"],
                ].map(([t,e],i) => (
                  <div key={i} className="activity-row projekt-fade" style={{"--p-delay": `${0.55 + i * 0.1}s`}}>
                    <span className="mono" style={{color:'var(--ink-4)', fontSize:11.5, width:42, flexShrink:0}}>{t}</span>
                    <span style={{fontSize:13}}>{e}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-head">
                <span style={{fontWeight:600, fontSize:14}}>ÄTA-arbeten</span>
                <span className="mono" style={{fontSize:12, color:'var(--accent-green)'}}>+12 400 kr</span>
              </div>
              <div className="ata-row projekt-fade" style={{"--p-delay":"0.6s"}}>
                <div>
                  <div style={{fontSize:13, fontWeight:500}}>Förstärka bjälklag</div>
                  <div style={{fontSize:11.5, color:'var(--ink-4)', marginTop:2}}>Godkänd 24 maj · 6 tim</div>
                </div>
                <span className="mono" style={{fontSize:13}}>8 200 kr</span>
              </div>
              <div className="ata-row projekt-fade" style={{"--p-delay":"0.72s"}}>
                <div>
                  <div style={{fontSize:13, fontWeight:500}}>Extra eluttag, hall</div>
                  <div style={{fontSize:11.5, color:'var(--ink-4)', marginTop:2}}>Väntar besked · 2 tim</div>
                </div>
                <span className="mono" style={{fontSize:13, color:'var(--ink-3)'}}>4 200 kr</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const KPI = ({label, value, suffix, sub, warn, delay}) => (
  <div className="kpi-card projekt-fade" style={{"--p-delay": `${0.1 + delay * 0.1}s`}}>
    <div style={{fontSize:11, fontFamily:'var(--font-mono)', textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--ink-4)'}}>{label}</div>
    <div style={{fontFamily:'var(--font-display)', fontSize:24, fontWeight:600, marginTop:6, letterSpacing:'-0.02em', color: warn ? 'var(--primary)' : 'var(--ink-1)'}}>
      <span className="tnum">{value}</span>
      {suffix && <span style={{fontSize:13, color:'var(--ink-3)', marginLeft:4, fontWeight:400}}>{suffix}</span>}
    </div>
    {sub && <div style={{fontSize:12, color:'var(--ink-3)', marginTop:4}}>{sub}</div>}
  </div>
);

Object.assign(window, { ProjektScreen });
